import { t, Trans } from '@lingui/macro';

import { Alert } from '@decisiv/ui-components';
import Avatar from '@decisiv/ui-components/lib/components/Avatar';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import {
  BannerContainer,
  BannerContent,
  ContactCard,
  TitleActionButton,
} from './styles';

interface ExistingContact {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  mobilePhone?: string | null;
  mobilePhoneCountryCode?: string | null;
}

type ExistingContactBannerProps = {
  contact: ExistingContact;
  loading?: boolean;
} & (
  | {
      existingContactInDepot: true;
      onEditExistingContact: () => void;
      onAddExistingContact?: () => void;
    }
  | {
      existingContactInDepot?: false;
      onAddExistingContact: () => void;
      onEditExistingContact?: never;
    }
);

function formatPhone(contact: ExistingContact) {
  if (!contact.mobilePhone) return undefined;
  return `${contact.mobilePhoneCountryCode || ''} ${
    contact.mobilePhone
  }`.trim();
}

export default function ExistingContactBanner({
  contact,
  onAddExistingContact,
  onEditExistingContact,
  existingContactInDepot = false,
  loading = false,
}: ExistingContactBannerProps) {
  const name = `${contact.firstName} ${contact.lastName}`;
  const phone = formatPhone(contact);

  return (
    <BannerContainer>
      <TitleActionButton>
        <Button
          kind="primary"
          size="small"
          text={
            existingContactInDepot
              ? t`Edit Existing Contact`
              : t`Add Existing Contact`
          }
          onClick={
            existingContactInDepot
              ? onEditExistingContact
              : onAddExistingContact
          }
          loading={loading}
          disabled={loading}
        />
      </TitleActionButton>
      <Alert intent="warning" title={t`Contact Already Exists.`}>
        <BannerContent>
          <P size="small">
            <Trans>
              This email is already associated with an existing contact.
            </Trans>
            <br />
            {existingContactInDepot ? (
              <Trans>This contact is already part of this depot.</Trans>
            ) : (
              <Trans>Add the existing contact to this depot.</Trans>
            )}
          </P>
          <ContactCard>
            <Avatar name={name} />
            <Flex flex={1} flexDirection="column">
              <P weight="medium">{name}</P>
              {(contact.email || phone) && (
                <P size="small" shade={1}>
                  {[contact.email ? `<${contact.email}>` : null, phone]
                    .filter(Boolean)
                    .join(' \u00b7 ')}
                </P>
              )}
            </Flex>
          </ContactCard>
        </BannerContent>
      </Alert>
    </BannerContainer>
  );
}
